/**
 * Maps the Optis questions rendered on a product page to the clock
 * designer's payload fields, by the question's visible label rather than by
 * its auto-generated field id.
 *
 * Ids are generated per question per listing, so any id written into code is
 * wrong on every other listing — which is how a logo upload field hardcoded
 * to one listing silently dropped customers' logos on the other two. Labels
 * are what the shop owner controls in the Optis admin, so matching on them
 * means adding or duplicating a listing needs no code change.
 *
 * Deliberately free of page state, network calls and globals: everything
 * here is a pure function over a DOM node or a plain object, so it can be
 * unit tested in Node without booting the storefront asset.
 *
 * ES5-compatible and loaded as a plain theme asset — there is no build step.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ClockFieldMatcher = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  /** "po_text_area1391058" -> "text_area". Empty string if not an Optis field. */
  function widgetTypeOf(name) {
    var match = /^po_([a-z_]+?)\d+$/.exec(String(name || ''));
    return match ? match[1] : '';
  }

  /** Lowercased, whitespace-collapsed form used for keyword comparison. */
  function normalize(text) {
    return String(text == null ? '' : text).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /** Whitespace-collapsed text of an element, excluding any form controls.
   *
   * Form controls are stripped because a <select>'s options are part of its
   * wrapper's textContent — read naively, a question's "label" comes back as
   * the label plus every option in the dropdown. Original casing is kept so
   * the debug report can show the owner the label as it appears on the page.
   */
  function ownText(el) {
    if (!el || !el.cloneNode) return '';
    var clone = el.cloneNode(true);
    var controls = clone.querySelectorAll('select, option, textarea, input, button, script, style');
    for (var i = 0; i < controls.length; i++) {
      controls[i].parentNode.removeChild(controls[i]);
    }
    return String(clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /** How many ancestors to search for question text before giving up.
   *
   * Bounded deliberately. Climbing to <body> always finds text — every other
   * question on the page — which would match whichever field is tried first
   * and mismatch every field after it. A wrong match is worse than no match,
   * because no match is reported and a wrong one is not.
   */
  var MAX_ANCESTOR_DEPTH = 4;

  /** Whether an element is the upload widget's own chrome, not a question's
   * label — Optis's dropzone wraps a file input in a real <label for="...">
   * ("Choose file / Or drop file to upload / Max 50 MB"), and that label is
   * also the input's nearest ancestor. Both the label[for] lookup and the
   * ancestor walk below would otherwise return this on every single file
   * upload question, which is a confident wrong answer, not a missing one —
   * exactly the failure this function exists to avoid. The class name is a
   * stable Optis convention; the English wording is not.
   */
  function isDropzoneChrome(el) {
    return !!el && typeof el.className === 'string' && el.className.toLowerCase().indexOf('dropzone') !== -1;
  }

  /** aria-labels that describe the control rather than the question.
   *
   * A file input very often carries aria-label="Choose File" from the theme or
   * the browser's own chrome. Trusting it resolves the logo question to
   * "Choose File" — a confident wrong answer on the one field whose silent
   * breakage prompted this rewrite — where falling through to the next
   * strategy finds the real question text.
   */
  var GENERIC_ARIA_LABELS = [
    'choose file', 'choose files', 'browse', 'browse...', 'file', 'files',
    'upload', 'select file', 'select files', 'no file chosen', 'attach file'
  ];

  /** How many distinct Optis questions live inside an element.
   *
   * Counts distinct field NAMES, not elements. A checkbox question is several
   * inputs sharing one name, so counting elements would read one question as
   * several and reject a label that legitimately belongs to it.
   *
   * The same dedupe is why <option> is harmless here and simply left out of
   * the selector for tidiness: the Optis dropdown widget stamps its <select>'s
   * name onto every <option> too (see getOptisValues in clock-designer.js —
   * that cost one shipped bug), but identical names collapse to a single
   * entry, so including them would not change any count.
   */
  function distinctQuestionCount(el) {
    var nodes = el.querySelectorAll(
      'select[name^="po_"], input[name^="po_"], textarea[name^="po_"]'
    );
    var seen = {};
    var count = 0;
    for (var i = 0; i < nodes.length; i++) {
      var name = nodes[i].getAttribute('name');
      if (name && !seen[name]) {
        seen[name] = true;
        count++;
      }
    }
    return count;
  }

  /** Text of the nearest ancestor holding any of its own, up to `limit` levels.
   *
   * `limit` is separate from MAX_ANCESTOR_DEPTH so the group-label branch can
   * search a strictly shorter distance than the general case.
   */
  function nearestAncestorText(input, limit) {
    var node = input.parentElement;
    var depth = 0;
    while (node && depth < limit) {
      // Skip the dropzone's own chrome rather than returning it — see
      // isDropzoneChrome. It always has non-empty ownText ("Choose file..."),
      // so without this it wins here before a real ancestor is ever reached.
      var text = isDropzoneChrome(node) ? '' : ownText(node);
      if (text) return text;
      node = node.parentElement;
      depth++;
    }
    return '';
  }

  /** The question text for an input, by the first strategy that yields any.
   *
   * aria-label, then a label[for], then an ancestor <label>, then the nearest
   * ancestor holding text of its own.
   */
  function resolveLabel(input) {
    if (!input) return '';
    var aria = input.getAttribute && input.getAttribute('aria-label');
    if (aria && aria.trim() && GENERIC_ARIA_LABELS.indexOf(normalize(aria)) === -1) {
      // Collapsed, not just trimmed: this string is shown to the shop owner in
      // a diagnostic report, and every other strategy returns collapsed text.
      return aria.trim().replace(/\s+/g, ' ');
    }

    var doc = input.ownerDocument;
    if (doc && input.id) {
      // Compared via htmlFor rather than a [for="..."] selector: ids here are
      // app-generated and may contain characters a selector would need
      // escaping for.
      var labels = doc.getElementsByTagName('label');
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].htmlFor === input.id && !isDropzoneChrome(labels[i])) {
          var forText = ownText(labels[i]);
          if (forText) return forText;
        }
      }
    }

    // No <fieldset>/<legend> strategy here yet, deliberately. A checkbox or
    // radio group's per-choice <label> holds a choice name ("Modern") while
    // the question ("Clock Style") would be the fieldset's legend — so this
    // shape currently resolves to a choice name, which is a known gap.
    //
    // An earlier attempt at a legend strategy was removed: unguarded, it made
    // every checkbox question inside one broad fieldset resolve to that
    // fieldset's legend, which is the identical-labels failure this function
    // works hard to avoid. Guarding it properly needs to know whether Optis
    // emits fieldsets at all, and with what nesting — which the fixture task
    // establishes from the three live listings' real markup. Until then, no
    // strategy is better than a wrong one.

    // Bounded, unlike closest('label'): the same depth reasoning as the
    // ancestor search below applies here too — a <label> far up the tree is a
    // section heading, not this question's label.
    var labelNode = input.parentElement;
    var labelDepth = 0;
    while (labelNode && labelDepth < MAX_ANCESTOR_DEPTH) {
      if (labelNode.tagName === 'LABEL' && !isDropzoneChrome(labelNode)) {
        if (distinctQuestionCount(labelNode) <= 1) {
          var wrappingText = ownText(labelNode);
          if (wrappingText) return wrappingText;
          // Note: with nested <label>s — invalid HTML, but parsers accept them —
          // this falls out to an outer group label and both questions resolve to
          // its text. Left unguarded deliberately; valid markup cannot nest
          // labels, which is why a span or div is the only valid way to caption a
          // row inside a group label.
          break;
        }
        // A group label wraps several questions, so its own text describes all
        // of them and must not be used for any one. Ancestors at or above it
        // are supersets of that text and are no better — but ancestors BELOW
        // it can hold a caption for this question alone, which is exactly what
        // a row wrapper inside a group label is. Search only those: at
        // labelDepth 0 there are none, so a bare group label still yields ''.
        return nearestAncestorText(input, labelDepth);
      }
      labelNode = labelNode.parentElement;
      labelDepth++;
    }

    return nearestAncestorText(input, MAX_ANCESTOR_DEPTH);
  }

  /** Every Optis question on the page, as {name, type, label, element}.
   *
   * Scoped to real form controls: this app stamps name="po_dropdown<id>" onto
   * each <option> as well as the <select>, so a bare [name^="po_"] selector
   * walks into the options too. Deduplicated by name because a checkbox or
   * radio question is several inputs sharing one name.
   *
   * KNOWN GAP: the dedupe keeps the FIRST occurrence in document order. Shopify
   * themes often render the product form twice — a quick-add modal or sticky
   * add-to-cart bar alongside the main form — and a hidden copy frequently comes
   * first in source order. If the two copies carry different label text, the
   * hidden one wins and resolves a confidently wrong label; display:none gives
   * nothing to filter on, because textContent ignores CSS. Scoping this query to
   * the designer's own enclosing <form> would fix it, but which element that is
   * cannot be known until the real markup is captured — see the fixture task.
   */
  function describeQuestions(doc) {
    var nodes = doc.querySelectorAll(
      'select[name^="po_"], input[name^="po_"], textarea[name^="po_"]'
    );
    var seen = {};
    var questions = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var name = el.getAttribute('name');
      var type = widgetTypeOf(name);
      if (!type || seen[name]) continue;
      seen[name] = true;
      questions.push({
        name: name,
        type: type,
        label: resolveLabel(el),
        element: el,
      });
    }
    return questions;
  }

  return {
    widgetTypeOf: widgetTypeOf,
    normalize: normalize,
    ownText: ownText,
    resolveLabel: resolveLabel,
    describeQuestions: describeQuestions,
  };
});
